import { config } from '@abyss/web/tools/config';
import { storage } from '@abyss/web/tools/storage';

import { Constants } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { getFeatureFlags } from '../../common/Utils';
import { Locale } from '../../frontends/ProviderSearch/context/Internationalization/types';
import { fetchJSON } from '../../lagoon/lagoonUtil';
import { useFeatureFlag } from '../useFeatureFlag';

export const useSupportedLanguages = (): string[] => {
  const [
    enableBurmeseLanguage,
    enableCreoleLanguage,
    enableTraditionalChineseLanguage,
    enableVietnameseLanguage,
    enableIlocanoLanguage,
    enableKoreanLanguage,
  ] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_BURMESE_LANGUAGE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_CREOLE_LANGUAGE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_TRADITIONAL_CHINESE_LANGUAGE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIETNAMESE_LANGUAGE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_ILOCANO_LANGUAGE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_KOREAN_LANGUAGE,
  ]);
  const supportedLanguages: string[] = [
    Locale.enUS,
    Locale.esUS,
    ...(enableBurmeseLanguage ? [Locale.myMM] : []),
    ...(enableCreoleLanguage ? [Locale.htHT] : []),
    ...(enableTraditionalChineseLanguage ? [Locale.zhTW] : []),
    ...(enableVietnameseLanguage ? [Locale.viVN] : []),
    ...(enableIlocanoLanguage ? [Locale.ilPH] : []),
    ...(enableKoreanLanguage ? [Locale.koUS] : []),
  ];

  storage.session.set(
    Constants.STORAGE_KEYS.SESSION.SUPPORTED_LANGUAGES,
    JSON.stringify(Array.from(supportedLanguages))
  );

  return supportedLanguages;
};

const parseLOB = (lineOfBusiness: string) => {
  switch (lineOfBusiness.toLowerCase()) {
    case 'e&i':
      return 'eni';
    case 'm&r':
      return 'mnr';
    case 'c&s':
      return 'cns';
    case 'ifp':
      return 'ifp';
    case 'obh':
      return 'obh';
    case 'umr':
      return 'umr';
    case 'guest': // Used for IFP Early Pay
      return 'guest';
    case 'e&i-guest':
      return 'guest-eni';
    case 'm&r-guest':
      return 'guest-mnr';
    case 'c&s-guest':
      return 'guest-cns';
    case 'ifp-guest':
      return 'guest-ifp';
    case 'guest-plan-selection':
      return 'guest-plan-selection';
    default:
      return 'eni';
  }
};

export const getSupportedLanguages = async (lob: string) => {
  const env = config('LAGOON_ENV');
  const parsedLob = parseLOB(lob);
  const urlRedirect = 'lagoon-web-psx.uhc.com';
  const enLocaleUrl = `https://${urlRedirect}/${parsedLob}/${env}/en/en.json`;
  const enLocaleData = await fetchJSON(enLocaleUrl);
  const [
    enableBurmeseLanguage,
    enableCreoleLanguage,
    enableTraditionalChineseLanguage,
    enableVietnameseLanguage,
    enableIlocanoLanguage,
    enableKoreanLanguage,
  ]: boolean[] = getFeatureFlags(
    enLocaleData[Constants.LAGOON_TABLE.FEATURE_FLAGS],
    [
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_BURMESE_LANGUAGE,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_CREOLE_LANGUAGE,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_TRADITIONAL_CHINESE_LANGUAGE,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_VIETNAMESE_LANGUAGE,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_ILOCANO_LANGUAGE,
      ConstantsLagoon.FEATURE_FLAGS.ENABLE_KOREAN_LANGUAGE,
    ]
  );

  const supportedLanguages: string[] = [
    Locale.enUS,
    Locale.esUS,
    ...(enableBurmeseLanguage ? [Locale.myMM] : []),
    ...(enableCreoleLanguage ? [Locale.htHT] : []),
    ...(enableTraditionalChineseLanguage ? [Locale.zhTW] : []),
    ...(enableVietnameseLanguage ? [Locale.viVN] : []),
    ...(enableIlocanoLanguage ? [Locale.ilPH] : []),
    ...(enableKoreanLanguage ? [Locale.koUS] : []),
  ];
  return supportedLanguages;
};
